import React from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
          data-testid="privacy-back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: March 26, 2026</p>

        <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">1. Introduction</h2>
            <p>
              M&J Enterprises ("we", "our", or "us"), located at 1-B, Rajupalem, Ozili Mandalam,
              Tirupathi District, Andhra Pradesh, India, operates the MJ Enterprises mobile application
              (the "App"). This Privacy Policy explains how we collect, use, disclose, and safeguard
              your information when you use our App.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">2. Information We Collect</h2>
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.1 Personal Information</h3>
            <p>When you use our App, we may collect the following personal information:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Name and email address (for account creation and authentication)</li>
              <li>Phone number (for driver and employee contact information)</li>
              <li>Business information (company name, GST details, addresses)</li>
              <li>Employee details (salary, overtime, advances, deductions)</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.2 Business Data</h3>
            <p>The App collects and stores business-related data including:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Silica load shipment details (weights, dates, vehicle numbers)</li>
              <li>Invoice records and payment information</li>
              <li>Expense records and categorizations</li>
              <li>Financial summaries and reports</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.3 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Device type and operating system</li>
              <li>App usage data and access timestamps</li>
              <li>IP address for security purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>To provide and maintain the App's functionality</li>
              <li>To manage user accounts and authentication</li>
              <li>To generate invoices, reports, and business documents</li>
              <li>To process employee payroll and track expenses</li>
              <li>To send email notifications (invoice delivery, payment reminders)</li>
              <li>To create and maintain data backups</li>
              <li>To improve the App and user experience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">4. Data Storage and Security</h2>
            <p>
              Your data is stored securely on cloud servers with industry-standard encryption.
              We implement appropriate technical and organizational measures to protect your
              personal information against unauthorized access, alteration, disclosure, or destruction.
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>All data transmission is encrypted using HTTPS/TLS</li>
              <li>Passwords are hashed and never stored in plain text</li>
              <li>Two-factor authentication (TOTP) is available for enhanced security</li>
              <li>Role-based access control limits data visibility</li>
              <li>Files and documents are stored in encrypted cloud storage (Cloudflare R2)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">5. Data Sharing</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties.
              Your data may be shared only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>With your consent:</strong> When you explicitly authorize sharing</li>
              <li><strong>Service providers:</strong> Cloud infrastructure providers who assist in operating the App (subject to confidentiality agreements)</li>
              <li><strong>Legal requirements:</strong> When required by law, regulation, or legal process</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">6. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as
              needed to provide you services. Business records (invoices, expenses, load data)
              are retained as required by applicable Indian tax and business laws. You may request
              deletion of your account and associated data by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your account and personal data</li>
              <li>Export your data in a machine-readable format</li>
              <li>Withdraw consent for data processing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">8. Children's Privacy</h2>
            <p>
              Our App is not intended for use by individuals under the age of 18. We do not
              knowingly collect personal information from children. If we become aware that we
              have collected data from a child, we will take steps to delete such information promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any
              changes by posting the new Privacy Policy within the App and updating the "Last
              updated" date. Continued use of the App after changes constitutes acceptance of
              the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">10. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, please contact us:</p>
            <div className="bg-slate-50 rounded-lg p-4 mt-3">
              <p className="font-semibold text-slate-900">M&J Enterprises</p>
              <p>1-B, Rajupalem, Ozili Mandalam</p>
              <p>Tirupathi District, Andhra Pradesh, India</p>
              <p className="mt-2">
                Email:{" "}
                <a href="mailto:info@mjenterprises.net" className="text-blue-600 hover:underline">
                  info@mjenterprises.net
                </a>
              </p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-200 text-center text-sm text-slate-400">
          &copy; {new Date().getFullYear()} M&J Enterprises. All rights reserved.
        </div>
      </div>
    </div>
  );
}
